/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { SeasonInfo } from 'client-code/episode-guide';

const MAX_ITEM_IN_PAGE_ON_DEMAND = 19;
const SEASON_HEADER_SIZE = 2;

/**
 * Converts a page number to the corresponding episode offset in the pure episode list.
 *
 * @param page - Page number in the composite list
 * @param seasons - SeasonsInfo
 * @returns Episode offset for the start of the page, or -1 if out of bounds
 */
export const getOnDemandOffsetFromPage = (page: number, seasons: SeasonInfo[]): number => {
    let space = page * MAX_ITEM_IN_PAGE_ON_DEMAND;
    let episodeIndex = 0;

    // eslint-disable-next-line no-restricted-syntax
    for (const season of seasons) {
        // season header
        if (space < SEASON_HEADER_SIZE) return episodeIndex;
        space -= SEASON_HEADER_SIZE;

        // season episodes
        if (space < season.episodeCount) return episodeIndex + space;
        space -= season.episodeCount;
        episodeIndex += season.episodeCount;
    }

    return -1; // (Out of bound)
};

/**
 * Converts a page number and position to the corresponding index within that page.
 *
 * @param pageNo - Page number in the composite list
 * @param pageSpaceIndex - Space Position within the page (calibrated for season headers)
 * @param seasons - Array of season information with episode counts
 * @returns Index within the specified page, or -1 if out of bounds
 */
export const getOnDemandPageIndex = (
    pageNo: number,
    pageSpaceIndex: number,
    seasons: SeasonInfo[],
): number => {
    const targetSpace = pageNo * MAX_ITEM_IN_PAGE_ON_DEMAND + pageSpaceIndex;
    const pageStart = pageNo * MAX_ITEM_IN_PAGE_ON_DEMAND;

    let spaceCursor = 0;
    let pageIndex = 0;

    // eslint-disable-next-line no-restricted-syntax
    for (const season of seasons) {
        // Season header
        if (spaceCursor >= pageStart && spaceCursor < targetSpace) pageIndex += 1;
        if (targetSpace < spaceCursor + SEASON_HEADER_SIZE) return pageIndex;
        spaceCursor += SEASON_HEADER_SIZE;

        // Episode processing
        const epStart = spaceCursor;
        const epEnd = spaceCursor + season.episodeCount;
        if (targetSpace < epEnd) {
            // If pageIndex is still zero that means there is no season picker in the target page
            // so that page pageSpaceInder can be used directly as the index
            return pageIndex === 0 ? pageSpaceIndex : pageIndex + (targetSpace - epStart);
        }
        if (epEnd > pageStart) {
            pageIndex += Math.max(0, epEnd - Math.max(epStart, pageStart));
        }
        spaceCursor = epEnd;
    }

    return -1; // (Out of bound)
};

/**
 * Calculates the page number and position within that page for a given episode index.
 *
 * @param episodeIndex - Index of the episode in the pure episode list
 * @param seasons - SeasonInfo
 * @returns Pageno and position (space calibrated for season headers) within the page
 */
export const getOnDemandPage = (
    episodeIndex: number,
    seasons: SeasonInfo[],
): { page: number; pageSpaceIndex: number } => {
    let page = 0;
    let usedSpace = 0;
    let episodesSoFar = 0;

    // eslint-disable-next-line no-restricted-syntax
    for (const season of seasons) {
        // Season header processing
        if (usedSpace + SEASON_HEADER_SIZE > MAX_ITEM_IN_PAGE_ON_DEMAND) {
            page += 1;
            usedSpace = 0;
        }
        usedSpace += SEASON_HEADER_SIZE;

        // Episode belong to this season?
        if (episodeIndex < episodesSoFar + season.episodeCount) {
            const totalSpaceNeeded = usedSpace + episodeIndex - episodesSoFar;
            // Page jump
            page += Math.floor(totalSpaceNeeded / MAX_ITEM_IN_PAGE_ON_DEMAND);
            usedSpace = totalSpaceNeeded % MAX_ITEM_IN_PAGE_ON_DEMAND;
            return { page, pageSpaceIndex: usedSpace };
        }

        // Page jump
        page += Math.floor((usedSpace + season.episodeCount) / MAX_ITEM_IN_PAGE_ON_DEMAND);
        usedSpace = (usedSpace + season.episodeCount) % MAX_ITEM_IN_PAGE_ON_DEMAND;
        episodesSoFar += season.episodeCount;
    }

    return { page, pageSpaceIndex: usedSpace }; // Technically should not reach here. (Out of bound)
};
